import { useCallback, useEffect, useRef, useState } from 'react';
import { getProgressOwner } from '../../../utils/session';
import { loadProgress, saveProgress, clearProgress } from '../../../utils/exerciseProgress';

// =============================================================
// 演習の進行状態（現在ステップ・到達済み・選択部品・完了）をまとめて持つフック。
// Ex1_2026_Shape / Ex1_2026_Gaze が同じ状態パターンを持っていたので共通化した。
//
// 状態はログイン中の番号ごとに localStorage へ保存し、リロードやタブの誤操作で
// 中断しても同じ番号でログインし直せば続きから再開できる。番号なし（ゲスト）は
// そのタブの間だけ sessionStorage に保存する（リロードで問1に戻らないように。2026-10-12）。
//
// 回答済みの記録は【決定】を押した時点で付ける（markStepAnswered）。次の問へ進むのは
// 「次の問題に進みます」の【進む】（completeStep）。こうしておくと、その間にリロードしても
// 答え直しにならず、開き直すと「まだ答えていない最初のステップ」から再開する。
//
// 回答（部品選択・自己説明の決定）が済んだステップは completedSteps に記録し、
// 回答内容そのものも answers に残す。タブで前の問へ戻ったときは、
// **そのときの操作結果（着色・プルダウンの選択）を復元して表示し、決定ボタンだけ
// 押せなくする**（回答は送信済みなので二重送信させず、見直しはできるようにする）。
//
// extra は演習ごとの追加状態（Shape のカメラ位置など）を入れる自由枠。
// JSON にできる値だけを入れること（保存対象のため）。
// =============================================================

function makeInitialState(questions, totalSteps) {
  return {
    index: 0,
    maxReachedIndex: 0,
    isFinished: false,
    selectedParts: Array(questions).fill(''),
    completedSteps: Array(totalSteps).fill(false),
    // ステップ番号 → 自己説明の回答内容（{ text, selections }）。戻ったときの再表示用
    answers: {},
    extra: {},
  };
}

function restoreState(saved, questions, totalSteps) {
  // 問数が変わった（modelMap を編集した）保存データは復元しない
  if (!saved || saved.questions !== questions) return null;
  const clamp = (v) => (Number.isFinite(v) ? Math.min(Math.max(v, 0), totalSteps - 1) : 0);
  const parts = Array.isArray(saved.selectedParts) ? saved.selectedParts : [];
  const done = Array.isArray(saved.completedSteps) ? saved.completedSteps : [];
  const state = {
    index: clamp(saved.index),
    maxReachedIndex: clamp(saved.maxReachedIndex),
    isFinished: !!saved.isFinished,
    selectedParts: Array.from({ length: questions }, (_, i) => parts[i] || ''),
    completedSteps: Array.from({ length: totalSteps }, (_, i) => !!done[i]),
    answers: saved.answers && typeof saved.answers === 'object' ? saved.answers : {},
    extra: saved.extra && typeof saved.extra === 'object' ? saved.extra : {},
  };
  // 回答済みのステップで止まっていたら（【決定】後の「次の問題に進みます」でリロードした・
  // 見直しのタブを開いたまま閉じた）、まだ答えていない最初のステップから再開する
  if (!state.isFinished && state.completedSteps[state.index]) {
    const next = state.completedSteps.findIndex((d) => !d);
    if (next === -1) state.isFinished = true;
    else {
      state.index = next;
      state.maxReachedIndex = Math.max(state.maxReachedIndex, next);
    }
  }
  return state;
}

export function useExerciseProgress({ exerciseKey, questions, stepsPerQuestion = 2 }) {
  const totalSteps = questions * stepsPerQuestion;
  // 保存先の持ち主（番号、または番号なしで進めているタブ）
  const owner = getProgressOwner();

  // 「どのステップから再開したか」。再開の案内を出す間だけ使う（進むと消える）
  const restoredIndexRef = useRef(null);

  const [state, setState] = useState(() => {
    const restored = restoreState(loadProgress(owner, exerciseKey), questions, totalSteps);
    if (!restored) return makeInitialState(questions, totalSteps);
    if (restored.index > 0) restoredIndexRef.current = restored.index;
    return restored;
  });

  useEffect(() => {
    saveProgress(owner, exerciseKey, { ...state, questions });
  }, [state, owner, exerciseKey, questions]);

  /** 回答（部品選択・自己説明の決定）が済んだステップか。 */
  const isStepCompleted = useCallback(
    (targetIndex) => !!state.completedSteps[targetIndex],
    [state.completedSteps]
  );

  /** 回答済みステップの自己説明の内容（戻ったときに復元して見せる）。 */
  const getStepAnswer = useCallback(
    (targetIndex) => state.answers[targetIndex] || null,
    [state.answers]
  );

  /** そのステップを開いてよいか（到達済みなら戻って見直せる）。 */
  const canGoToIndex = useCallback(
    (targetIndex) => targetIndex >= 0 && targetIndex <= state.maxReachedIndex,
    [state.maxReachedIndex]
  );

  const goToIndex = useCallback((targetIndex) => {
    setState((s) => (targetIndex >= 0 && targetIndex <= s.maxReachedIndex ? { ...s, index: targetIndex } : s));
  }, []);

  const markReached = useCallback((targetIndex) => {
    setState((s) => ({ ...s, maxReachedIndex: Math.max(s.maxReachedIndex, targetIndex) }));
  }, []);

  const setSelectedPart = useCallback((questionIdx, part) => {
    setState((s) => {
      const selectedParts = [...s.selectedParts];
      selectedParts[questionIdx] = part;
      return { ...s, selectedParts };
    });
  }, []);

  const updateExtra = useCallback((updater) => {
    setState((s) => ({ ...s, extra: updater(s.extra) }));
  }, []);

  /**
   * 現在ステップを回答済みにする（次へは進まない）。【決定】を押した時点で呼ぶ：
   * このあと「次の問題に進みます」の間にリロードしても、答え直しにならないようにするため。
   */
  const markStepAnswered = useCallback((selectedPart, answer = null) => {
    setState((s) => {
      const next = { ...s, completedSteps: [...s.completedSteps] };
      next.completedSteps[s.index] = true;
      if (answer) next.answers = { ...s.answers, [s.index]: answer };
      if (typeof selectedPart === 'string' && selectedPart) {
        next.selectedParts = [...s.selectedParts];
        next.selectedParts[Math.floor(s.index / stepsPerQuestion)] = selectedPart;
      }
      return next;
    });
  }, [stepsPerQuestion]);

  /**
   * 現在ステップを完了して次へ。最後のステップなら完了状態にする。
   * answer（自己説明の回答内容）を渡すと、戻ったときの再表示用に保存する。
   */
  const completeStep = useCallback((selectedPart, answer = null) => {
    setState((s) => {
      const next = { ...s };
      if (typeof selectedPart === 'string' && selectedPart) {
        const questionIdx = Math.floor(s.index / stepsPerQuestion);
        next.selectedParts = [...s.selectedParts];
        next.selectedParts[questionIdx] = selectedPart;
      }
      // 回答済みとして記録する（以降このステップの決定ボタンは押せなくなる）
      next.completedSteps = [...s.completedSteps];
      next.completedSteps[s.index] = true;
      if (answer) next.answers = { ...s.answers, [s.index]: answer };
      if (s.index >= totalSteps - 1) {
        next.isFinished = true;
        return next;
      }
      next.index = Math.min(s.index + 1, totalSteps - 1);
      next.maxReachedIndex = Math.max(s.maxReachedIndex, next.index);
      return next;
    });
  }, [stepsPerQuestion, totalSteps]);

  /**
   * 最初からやり直す（保存済みの進行状態も消す）。
   * 学習者の画面からは呼ばない（2026-10-12に完了画面の【最初からやり直す】を外した。答え直しを防ぐため）。
   */
  const restart = useCallback(() => {
    clearProgress(owner, exerciseKey);
    restoredIndexRef.current = null;
    setState(makeInitialState(questions, totalSteps));
  }, [owner, exerciseKey, questions, totalSteps]);

  return {
    index: state.index,
    questionIndex: Math.floor(state.index / stepsPerQuestion),
    stepWithin: state.index % stepsPerQuestion,
    maxReachedIndex: state.maxReachedIndex,
    isFinished: state.isFinished,
    selectedParts: state.selectedParts,
    extra: state.extra,
    totalSteps,
    // 再開直後のステップにいる間だけ true（案内表示の出し分け用）
    isResumedStep: restoredIndexRef.current != null && state.index === restoredIndexRef.current && !state.isFinished,
    isStepCompleted,
    getStepAnswer,
    canGoToIndex,
    goToIndex,
    markReached,
    setSelectedPart,
    updateExtra,
    markStepAnswered,
    completeStep,
    restart,
  };
}
